import { StatusPill, type StatusPillTone } from "nazare-ui/components";

import { CHANGELOG, type ChangelogEntryType } from "../changelog-data";
import { RevealOnScroll } from "../reveal-on-scroll";
import { useDocumentTitle } from "../use-document-title";
import styles from "./changelog.module.css";

const TYPE_META: Record<ChangelogEntryType, { label: string; tone: StatusPillTone }> = {
  feature: { label: "New", tone: "info" },
  improvement: { label: "Improved", tone: "success" },
  fix: { label: "Fixed", tone: "warning" },
  other: { label: "Other", tone: "neutral" },
};

/**
 * `/changelog` — release history for PCG Platform, generated from
 * `changelog-data.ts` (see that file's own doc comment: it's maintained by
 * `scripts/release.mjs`, not hand-edited per release). No live backend;
 * this is a static, build-time list like every other page in this app.
 *
 * `CHANGELOG` is already newest-first (new releases are PREPENDED — see
 * that file's doc comment), so this component renders it in array order
 * with no re-sorting.
 *
 * Rendered as a vertical timeline: a rail line down the left with a dot per
 * release, each release fading/sliding up into view via `<RevealOnScroll>`
 * as it scrolls into the viewport, staggered slightly release-to-release
 * (`delayMs`) so a batch of releases doesn't reveal as one flat block.
 */
export function ChangelogPage() {
  useDocumentTitle("Changelog");

  return (
    <div className={styles.page}>
      <p className={styles.description}>
        What's new in PCG Platform, release by release — newest first.
      </p>

      {CHANGELOG.length === 0 ? (
        <p className={styles.emptyState}>
          Nothing here yet — check back after the next release.
        </p>
      ) : (
        <div className={styles.timeline}>
          <div className={styles.timelineRail} aria-hidden="true" />
          {CHANGELOG.map((release, releaseIndex) => (
            <RevealOnScroll
              key={release.version}
              className={styles.release}
              delayMs={Math.min(releaseIndex, 3) * 80}
            >
              <span className={styles.releaseDot} aria-hidden="true" />
              <header className={styles.releaseHeader}>
                <h2 className={styles.releaseVersion}>{release.version}</h2>
                <span className={styles.releaseDate}>{release.date}</span>
              </header>
              <ul className={styles.entryList}>
                {release.entries.map((entry, index) => (
                  <li key={index} className={styles.entry}>
                    <StatusPill tone={TYPE_META[entry.type].tone} className={styles.entryTag}>
                      {TYPE_META[entry.type].label}
                    </StatusPill>
                    <span className={styles.entryText}>{entry.text}</span>
                  </li>
                ))}
              </ul>
            </RevealOnScroll>
          ))}
        </div>
      )}
    </div>
  );
}
